import React from "react";

import { usersAPI } from "../../shared/api";
import { useFormik } from "formik";

import { validate as validateEmailAddress } from "email-validator";

import classNames from "classnames";

import type { User } from "../../shared/types/models";
import type { CreateUserFormValues } from "../../shared/types/components";
import type { APIErrors } from "../../../api/errors";
import type { FormikErrors } from "formik";

const { VITE_KING_ENERGY_USERS_EMAIL_DOMAIN: KING_ENERGY_USERS_EMAIL_DOMAIN } =
  import.meta.env;

function getUsername(emailAddress: string): string {
  return emailAddress.slice(0, emailAddress.lastIndexOf("@"));
}

function getHostname(emailAddress: string): string {
  return emailAddress.slice(emailAddress.lastIndexOf("@") + 1);
}

export interface CreateUserFormProps {
  initialValues?: CreateUserFormValues;
  handleCancelButtonClick?: () => void;
  handleFormSubmittingChange?: (isSubmitting: boolean) => void;
  handleFormSubmitSuccess?: (user: User) => void;
  handleFormSubmitFailure?: (error: unknown) => void;
}

export const CreateUserForm: React.FC<CreateUserFormProps> = (props) => {
  const [createUser] = usersAPI.endpoints.createUser.useMutation();

  const formik = useFormik<CreateUserFormValues>({
    initialValues: props.initialValues ?? {
      firstName: "",
      lastName: "",
      emailAddress: `@${KING_ENERGY_USERS_EMAIL_DOMAIN}`,
    },
    validate: (values) => {
      const errors: FormikErrors<CreateUserFormValues> = {};

      if (values.firstName.trim().length === 0) {
        errors.firstName = "First name is required";
      }

      if (values.lastName.trim().length === 0) {
        errors.lastName = "Last name is required";
      }

      if (values.emailAddress.trim().length === 0) {
        errors.emailAddress = "Email address is required";
      } else if (!validateEmailAddress(values.emailAddress)) {
        errors.emailAddress = "Email address must be valid";
      }

      return errors;
    },
    validateOnBlur: false,
    onSubmit: handleSubmit,
  });

  async function handleEmailAddressInputValueChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> {
    const username = event.target.value;

    await formik.setValues((values) => ({
      ...values,
      emailAddress: `${username}@${getHostname(values.emailAddress)}`,
    }));
  }

  function handleCancelButtonClick(): void {
    props.handleCancelButtonClick?.();
  }

  async function handleSubmit(values: CreateUserFormValues): Promise<void> {
    const result = await createUser({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      emailAddress: values.emailAddress,
    });

    if ("data" in result) {
      const user = result.data;

      props.handleFormSubmitSuccess?.(user);
    } else if ("error" in result) {
      const error = result.error as APIErrors;

      if (
        error.code === "entity-unique-constraint-broken" &&
        error.meta.entity.typeName === "User" &&
        "emailAddress" in error.meta.entity.keys
      ) {
        formik.setErrors({
          emailAddress: "Email address is already used",
        });
      }

      props.handleFormSubmitFailure?.(error);
    }
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="w-full max-w-md p-6 overflow-y-visible text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl"
    >
      <div className="flex justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">Create User</h3>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            First Name
          </label>
          <div className="mt-1">
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              value={formik.values.firstName}
              disabled={formik.isSubmitting}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={classNames(
                'mt-1 px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-0 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6',
                {
                  'border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring-blue-200/50 dark:focus:ring-blue-500/50': !(
                    formik.touched.firstName !== undefined &&
                    formik.touched.firstName &&
                    formik.errors.firstName !== undefined
                  ),
                  'border-red-300 dark:border-red-600 focus:border-red-300 dark:focus:border-red-500 focus:ring-red-200/50 dark:focus:ring-red-500/50':
                    formik.touched.firstName !== undefined && formik.touched.firstName && formik.errors.firstName !== undefined,
                }
              )}
              aria-invalid={formik.touched.firstName !== undefined && formik.touched.firstName && formik.errors.firstName !== undefined ? 'true' : 'false'}
              aria-describedby={
                formik.touched.firstName !== undefined && formik.touched.firstName && formik.errors.firstName !== undefined ? 'first-name-error' : undefined
              }
            />
          </div>
          {formik.touched.firstName !== undefined && formik.touched.firstName && formik.errors.firstName !== undefined && (
            <p className="mt-1 text-sm text-red-500" id="first-name-error">
              {formik.errors.firstName}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="lastName" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Last Name
          </label>
          <div className="mt-1">
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              value={formik.values.lastName}
              disabled={formik.isSubmitting}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={classNames(
                'mt-1 px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-0 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6',
                {
                  'border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring-blue-200/50 dark:focus:ring-blue-500/50': !(
                    formik.touched.lastName !== undefined &&
                    formik.touched.lastName &&
                    formik.errors.lastName !== undefined
                  ),
                  'border-red-300 dark:border-red-600 focus:border-red-300 dark:focus:border-red-500 focus:ring-red-200/50 dark:focus:ring-red-500/50':
                    formik.touched.lastName !== undefined && formik.touched.lastName && formik.errors.lastName !== undefined,
                }
              )}
              aria-invalid={formik.touched.lastName !== undefined && formik.touched.lastName && formik.errors.lastName !== undefined ? 'true' : 'false'}
              aria-describedby={
                formik.touched.lastName !== undefined && formik.touched.lastName && formik.errors.lastName !== undefined ? 'last-name-error' : undefined
              }
            />
          </div>
          {formik.touched.lastName !== undefined && formik.touched.lastName && formik.errors.lastName !== undefined && (
            <p className="mt-1 text-sm text-red-500" id="last-name-error">
              {formik.errors.lastName}
            </p>
          )}
        </div>
        <div className="col-span-2">
          <label htmlFor="emailAddress" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Email Address
          </label>
          <div className="relative mt-1 flex rounded-md shadow-sm">
            <input
              id="emailAddress"
              name="emailAddress"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              value={getUsername(formik.values.emailAddress)}
              disabled={formik.isSubmitting}
              onChange={(event) => {
                void handleEmailAddressInputValueChange(event)
              }}
              onBlur={formik.handleBlur}
              className={classNames(
                'block w-full rounded-md border-0 py-1.5 pl-2 pr-[130px] text-gray-900 dark:text-gray-100 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6',
                {
                  'border-gray-300 dark:border-gray-600 focus:border-blue-300 dark:focus:border-blue-500 focus:ring-blue-200/50 dark:focus:ring-blue-500/50': !(
                    formik.touched.emailAddress !== undefined &&
                    formik.touched.emailAddress &&
                    formik.errors.emailAddress !== undefined
                  ),
                  'border-red-300 dark:border-red-600 focus:border-red-300 dark:focus:border-red-500 focus:ring-red-200/50 dark:focus:ring-red-500/50':
                    formik.touched.emailAddress !== undefined && formik.touched.emailAddress && formik.errors.emailAddress !== undefined,
                }
              )}
              aria-invalid={
                formik.touched.emailAddress !== undefined && formik.touched.emailAddress && formik.errors.emailAddress !== undefined ? 'true' : 'false'
              }
              aria-describedby={
                formik.touched.emailAddress !== undefined && formik.touched.emailAddress && formik.errors.emailAddress !== undefined
                  ? 'email-address-error'
                  : undefined
              }
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500 dark:text-gray-400 sm:text-sm" id="price-currency">
                @{getHostname(formik.values.emailAddress)}
              </span>
            </div>
          </div>
          {formik.touched.emailAddress !== undefined && formik.touched.emailAddress && formik.errors.emailAddress !== undefined && (
            <p className="mt-1 text-sm text-red-500" id="email-address-error">
              {formik.errors.emailAddress}
            </p>
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          disabled={formik.isSubmitting}
          onClick={handleCancelButtonClick}
          className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:cursor-not-allowed border border-transparent rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500 dark:focus-visible:ring-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={formik.isSubmitting}
          className="ml-2 inline-flex justify-center px-4 py-2 text-sm font-medium text-blue-900 dark:text-blue-100 bg-blue-100 dark:bg-blue-900 disabled:text-blue-500 dark:disabled:text-blue-400 disabled:bg-blue-50 dark:disabled:bg-blue-950 disabled:cursor-not-allowed border border-transparent rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
        >
          {formik.isSubmitting && (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          Save
        </button>
      </div>
    </form>
  )
};
